import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { userAPI, rbacAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import {
  Search, Shield, Edit2, Loader2, Save, RotateCcw, ShieldAlert, AlertTriangle, History, Layers, CalendarClock, Filter
} from 'lucide-react';

// Fallback module catalog (used only if the backend list fails to load).
const FALLBACK_MODULES = [
  { id: 'dashboard', name: 'Dashboard', category: 'core' },
  { id: 'employees', name: 'Employees', category: 'hr' },
  { id: 'leaves', name: 'Leave Management', category: 'hr' },
  { id: 'attendance', name: 'Attendance', category: 'hr' },
  { id: 'timesheets', name: 'Timesheets', category: 'hr' },
  { id: 'performance', name: 'Performance', category: 'hr' },
  { id: 'celebrations', name: 'Celebrations', category: 'hr' },
  { id: 'expenses', name: 'Expenses', category: 'finance' },
  { id: 'crm', name: 'CRM', category: 'sales' },
  { id: 'holidays', name: 'Holidays', category: 'core' },
];

const CATEGORIES = {
  core: { label: 'Core', color: 'bg-blue-100 text-blue-800' },
  self_service: { label: 'Self Service', color: 'bg-green-100 text-green-800' },
  hr: { label: 'HR', color: 'bg-purple-100 text-purple-800' },
  finance: { label: 'Finance', color: 'bg-yellow-100 text-yellow-800' },
  sales: { label: 'Sales', color: 'bg-pink-100 text-pink-800' },
  admin: { label: 'Admin', color: 'bg-red-100 text-red-800' },
};

export default function UserAccessPage() {
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState(FALLBACK_MODULES);
  const [roleDefaults, setRoleDefaults] = useState({}); // role -> [moduleId]
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [customModules, setCustomModules] = useState([]);
  const [hasCustomAccess, setHasCustomAccess] = useState(false);
  const [saving, setSaving] = useState(false);

  // Cleanup tool state
  const [showCleanup, setShowCleanup] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [applyingCleanup, setApplyingCleanup] = useState(false);

  // Access change log state
  const [showAccessLog, setShowAccessLog] = useState(false);
  const [accessLog, setAccessLog] = useState([]);
  const [accessLogLoading, setAccessLogLoading] = useState(false);
  const [accessLogActors, setAccessLogActors] = useState([]);
  const [filterActor, setFilterActor] = useState('');
  const [filterTarget, setFilterTarget] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  // Time-boxed access expiry (moduleId -> YYYY-MM-DD)
  const [moduleExpiry, setModuleExpiry] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, modulesRes, rolesRes] = await Promise.all([
        userAPI.getAll(),
        rbacAPI.getModules().catch(() => null),
        rbacAPI.getRoles().catch(() => null),
      ]);
      setUsers(usersRes.data);
      if (modulesRes?.data?.modules?.length) {
        setModules(modulesRes.data.modules);
      }
      if (rolesRes?.data?.roles?.length) {
        const map = {};
        rolesRes.data.roles.forEach((r) => { map[r.role] = r.modules || []; });
        setRoleDefaults(map);
      }
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Effective role modules come from the backend (single source of truth), so
  // presets and per-user defaults can never drift apart again.
  const getDefaultModulesForRole = (role) => {
    const mods = roleDefaults[role];
    if (!mods || !mods.length) return [];
    if (mods.includes('*')) return modules.map((m) => m.id);
    return [...mods];
  };

  const handleEditAccess = (userItem) => {
    setSelectedUser(userItem);
    const hasCustom = userItem.custom_modules !== undefined && userItem.custom_modules !== null;
    setHasCustomAccess(hasCustom);
    if (hasCustom && Array.isArray(userItem.custom_modules)) {
      setCustomModules(userItem.custom_modules);
    } else {
      setCustomModules(getDefaultModulesForRole(userItem.role));
    }
    setModuleExpiry(userItem.module_expiry || {});
    setShowDialog(true);
  };

  const toggleModule = (moduleId) => {
    setCustomModules((prev) =>
      prev.includes(moduleId) ? prev.filter((m) => m !== moduleId) : [...prev, moduleId]
    );
  };

  const selectAllCategory = (category) => {
    const categoryModules = modules.filter((m) => m.category === category).map((m) => m.id);
    setCustomModules((prev) => {
      const next = [...prev];
      categoryModules.forEach((id) => { if (!next.includes(id)) next.push(id); });
      return next;
    });
  };

  const deselectAllCategory = (category) => {
    const categoryModules = modules.filter((m) => m.category === category).map((m) => m.id);
    setCustomModules((prev) => prev.filter((m) => !categoryModules.includes(m)));
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await userAPI.updateAccess(selectedUser.id, {
        custom_modules: hasCustomAccess ? customModules : null,
        module_expiry: hasCustomAccess
          ? Object.fromEntries(
              Object.entries(moduleExpiry).filter(([m, d]) => d && customModules.includes(m))
            )
          : {},
      });
      toast.success(`Access updated for ${selectedUser.name}`);
      setShowDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update access');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToRole = () => {
    if (selectedUser) {
      setCustomModules(getDefaultModulesForRole(selectedUser.role));
      setHasCustomAccess(false);
    }
  };

  const openCleanup = async () => {
    setShowCleanup(true);
    setCleanupPreview(null);
    setCleanupLoading(true);
    try {
      const res = await rbacAPI.cleanupOverrides(true); // dry run preview
      setCleanupPreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to build cleanup preview');
      setShowCleanup(false);
    } finally {
      setCleanupLoading(false);
    }
  };

  const applyCleanup = async () => {
    setApplyingCleanup(true);
    try {
      const res = await rbacAPI.cleanupOverrides(false); // apply
      toast.success(`Cleaned overrides for ${res.data.users_affected} user(s)`);
      setShowCleanup(false);
      setCleanupPreview(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply cleanup');
    } finally {
      setApplyingCleanup(false);
    }
  };

  const moduleName = (id) => modules.find((m) => m.id === id)?.name || id;

  // Effective screens a user actually gets right now (custom override or role default)
  const effectiveModules = (userItem) => {
    if (Array.isArray(userItem.custom_modules)) {
      if (userItem.custom_modules.includes('*')) return modules.map((m) => m.id);
      return userItem.custom_modules;
    }
    return getDefaultModulesForRole(userItem.role);
  };

  const openAccessLog = async () => {
    setShowAccessLog(true);
    await loadAccessLog();
  };

  const loadAccessLog = async () => {
    setAccessLogLoading(true);
    try {
      const params = { limit: 200 };
      if (filterActor) params.actor_user_id = filterActor;
      if (filterTarget) params.entity_id = filterTarget;
      if (filterFrom) params.from_date = filterFrom;
      if (filterTo) params.to_date = filterTo;
      const res = await rbacAPI.getAccessLog(params);
      setAccessLog(res.data.logs || []);
      setAccessLogActors(res.data.actors || []);
    } catch (error) {
      toast.error('Failed to load access change log');
    } finally {
      setAccessLogLoading(false);
    }
  };

  const clearLogFilters = async () => {
    setFilterActor('');
    setFilterTarget('');
    setFilterFrom('');
    setFilterTo('');
    setAccessLogLoading(true);
    try {
      const res = await rbacAPI.getAccessLog({ limit: 200 });
      setAccessLog(res.data.logs || []);
      setAccessLogActors(res.data.actors || []);
    } finally {
      setAccessLogLoading(false);
    }
  };

  const logDiff = (log) => {
    const b = log.before_state || {};
    const a = log.after_state || {};
    const beforeMods = b.modules || b.custom_modules || [];
    const afterMods = a.modules || a.custom_modules || [];
    const added = (afterMods || []).filter((m) => !(beforeMods || []).includes(m));
    const removed = (beforeMods || []).filter((m) => !(afterMods || []).includes(m));
    return { added, removed };
  };

  const formatDate = (ts) => {
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  const filteredUsers = users.filter((u) =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  // Modules granted beyond the user's role default (candidates for time-boxing)
  const roleDefaultForSelected = selectedUser ? getDefaultModulesForRole(selectedUser.role) : [];
  const extraModules = customModules.filter((m) => !roleDefaultForSelected.includes(m));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-access-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope']">User Access Management</h1>
          <p className="text-muted-foreground">Customize screen access for individual users</p>
        </div>
        {isSuperAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={openAccessLog} data-testid="view-access-log-btn">
              <History className="w-4 h-4 mr-2" />
              Access Change Log
            </Button>
            <Button variant="outline" onClick={openCleanup} data-testid="cleanup-overrides-btn">
              <ShieldAlert className="w-4 h-4 mr-2" />
              Clean Up Invalid Overrides
            </Button>
          </div>
        )}
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">How User Access Works</p>
              <p className="text-sm text-blue-700 mt-1">
                By default, users inherit permissions from their role. You can customize access for individual users
                to grant or restrict specific screens. Custom access overrides role-based permissions.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative w-full md:w-72">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="user-access-search"
        />
      </div>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Access Type</TableHead>
                <TableHead>Effective Screens</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((userItem) => (
                  <TableRow key={userItem.id}>
                    <TableCell className="font-medium">{userItem.name}</TableCell>
                    <TableCell>{userItem.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{userItem.role}</Badge>
                    </TableCell>
                    <TableCell>
                      {userItem.custom_modules ? (
                        <Badge className="bg-purple-100 text-purple-800">Custom</Badge>
                      ) : (
                        <Badge variant="secondary">Role-based</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const eff = effectiveModules(userItem);
                        return (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 gap-1" data-testid={`effective-screens-${userItem.id}`}>
                                <Layers className="w-3.5 h-3.5" />
                                {eff.length} screen{eff.length === 1 ? '' : 's'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-72 max-h-80 overflow-y-auto">
                              <p className="text-xs font-medium text-muted-foreground mb-2">
                                Effective screens ({userItem.custom_modules ? 'custom' : 'role-based'})
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {eff.length === 0 ? (
                                  <span className="text-xs text-muted-foreground">No screens assigned</span>
                                ) : (
                                  eff.map((id) => {
                                    const exp = userItem.module_expiry?.[id];
                                    return (
                                      <Badge key={id} variant="secondary" className="text-xs">
                                        {moduleName(id)}{exp ? ` · until ${exp}` : ''}
                                      </Badge>
                                    );
                                  })
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditAccess(userItem)}
                        data-testid={`edit-access-${userItem.id}`}
                      >
                        <Edit2 className="w-4 h-4 mr-1" /> Edit Access
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Access Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Edit Access - {selectedUser?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedUser?.email} • Role: <span className="capitalize font-medium">{selectedUser?.role}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Custom Access Toggle */}
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="customAccess"
                  checked={hasCustomAccess}
                  onCheckedChange={(checked) => {
                    setHasCustomAccess(checked);
                    if (!checked) {
                      setCustomModules(getDefaultModulesForRole(selectedUser?.role));
                    }
                  }}
                  data-testid="toggle-custom-access"
                />
                <Label htmlFor="customAccess" className="font-medium cursor-pointer">
                  Enable Custom Access
                </Label>
              </div>
              {hasCustomAccess && (
                <Button variant="outline" size="sm" onClick={handleResetToRole}>
                  <RotateCcw className="w-4 h-4 mr-1" /> Reset to Role Default
                </Button>
              )}
            </div>

            {!hasCustomAccess && (
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800">
                  This user uses role-based permissions. Enable custom access to override specific screens.
                </p>
              </div>
            )}

            {/* Module Selection - only visible when custom access enabled */}
            {hasCustomAccess && (
              <div className="space-y-6">
                {Object.entries(CATEGORIES).map(([catKey, catInfo]) => {
                  const categoryModules = modules.filter((m) => m.category === catKey);
                  if (categoryModules.length === 0) return null;
                  const selectedCount = categoryModules.filter((m) => customModules.includes(m.id)).length;

                  return (
                    <div key={catKey} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge className={catInfo.color}>{catInfo.label}</Badge>
                          <span className="text-sm text-muted-foreground">
                            ({selectedCount}/{categoryModules.length} selected)
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => selectAllCategory(catKey)}>
                            Select All
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deselectAllCategory(catKey)}>
                            Deselect All
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {categoryModules.map((mod) => (
                          <div
                            key={mod.id}
                            className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                              customModules.includes(mod.id)
                                ? 'bg-primary/10 border-primary'
                                : 'bg-white hover:bg-muted/50'
                            }`}
                            onClick={() => toggleModule(mod.id)}
                            data-testid={`module-${mod.id}`}
                          >
                            <Checkbox
                              checked={customModules.includes(mod.id)}
                              onCheckedChange={() => toggleModule(mod.id)}
                            />
                            <span className="text-sm">{mod.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                {/* Time-boxed / temporary access */}
                <div className="space-y-2 border-t pt-4">
                  <div className="flex items-center gap-2">
                    <CalendarClock className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Temporary access (auto-expires)</span>
                  </div>
                  {extraModules.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      Grant a screen beyond this user's role above, then set an expiry date here. Time-boxed screens are auto-revoked after the date.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {extraModules.map((m) => (
                        <div key={m} className="flex items-center justify-between gap-2 p-2 rounded border bg-muted/30">
                          <span className="text-sm">{moduleName(m)}</span>
                          <div className="flex items-center gap-2">
                            <Input
                              type="date"
                              value={moduleExpiry[m] || ''}
                              onChange={(e) => setModuleExpiry((prev) => ({ ...prev, [m]: e.target.value }))}
                              className="h-8 w-40"
                              data-testid={`expiry-${m}`}
                            />
                            {moduleExpiry[m] ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-xs"
                                onClick={() => setModuleExpiry((prev) => { const n = { ...prev }; delete n[m]; return n; })}
                              >
                                Make permanent
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground">Permanent</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-access-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cleanup Overrides Dialog */}
      <Dialog open={showCleanup} onOpenChange={setShowCleanup}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-orange-600" />
              Clean Up Invalid Overrides
            </DialogTitle>
            <DialogDescription>
              Removes admin-only and invalid (ghost) modules from non-admin users' custom access.
              All other intentionally-granted access is preserved. Review before applying.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            {cleanupLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : cleanupPreview && cleanupPreview.changes?.length > 0 ? (
              <div className="space-y-3" data-testid="cleanup-preview">
                <div className="flex items-center gap-2 p-3 rounded-lg bg-orange-50 border border-orange-200">
                  <AlertTriangle className="w-4 h-4 text-orange-600 shrink-0" />
                  <p className="text-sm text-orange-800">
                    {cleanupPreview.users_affected} user(s) have invalid/admin modules that will be removed.
                  </p>
                </div>
                {cleanupPreview.changes.map((c) => (
                  <div key={c.id} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{c.name}</p>
                        <p className="text-xs text-muted-foreground">{c.email}</p>
                      </div>
                      <Badge variant="outline" className="capitalize">{c.role}</Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {c.removed.map((m) => (
                        <Badge key={m} className="bg-red-100 text-red-800 text-xs">
                          − {moduleName(m)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Shield className="w-10 h-10 text-green-600 mb-2" />
                <p className="font-medium">No invalid overrides found</p>
                <p className="text-sm text-muted-foreground">All custom access looks clean.</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCleanup(false)}>Close</Button>
            {cleanupPreview && cleanupPreview.changes?.length > 0 && (
              <Button
                onClick={applyCleanup}
                disabled={applyingCleanup}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="apply-cleanup-btn"
              >
                {applyingCleanup ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldAlert className="w-4 h-4 mr-2" />}
                Apply Cleanup
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access Change Log Dialog */}
      <Dialog open={showAccessLog} onOpenChange={setShowAccessLog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Access Change Log
            </DialogTitle>
            <DialogDescription>
              Who changed which role or user access, and when (most recent first).
            </DialogDescription>
          </DialogHeader>

          {/* Filters */}
          <div className="flex flex-wrap items-end gap-2 border-b pb-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Changed by</label>
              <select
                value={filterActor}
                onChange={(e) => setFilterActor(e.target.value)}
                className="h-9 rounded-md border bg-background px-2 text-sm min-w-[150px]"
                data-testid="log-filter-actor"
              >
                <option value="">Anyone</option>
                {accessLogActors.map((a) => (
                  <option key={a.id} value={a.id}>{a.name || a.email}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Target user</label>
              <select
                value={filterTarget}
                onChange={(e) => setFilterTarget(e.target.value)}
                className="h-9 rounded-md border bg-background px-2 text-sm min-w-[150px]"
                data-testid="log-filter-target"
              >
                <option value="">Any target</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">From</label>
              <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="h-9 w-36" data-testid="log-filter-from" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">To</label>
              <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="h-9 w-36" data-testid="log-filter-to" />
            </div>
            <Button size="sm" onClick={loadAccessLog} data-testid="log-apply-filters">
              <Filter className="w-4 h-4 mr-1" /> Apply
            </Button>
            <Button size="sm" variant="ghost" onClick={clearLogFilters}>Clear</Button>
          </div>

          <div className="py-2">
            {accessLogLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : accessLog.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <History className="w-10 h-10 text-muted-foreground mb-2" />
                <p className="font-medium">No access changes recorded yet</p>
                <p className="text-sm text-muted-foreground">Changes to roles and user access will appear here.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="access-log-list">
                {accessLog.map((log) => {
                  const { added, removed } = logDiff(log);
                  return (
                    <div key={log.id} className="border rounded-lg p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium">{log.entity_title || log.entity_id}</p>
                          <p className="text-xs text-muted-foreground">{log.remarks}</p>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(log.timestamp)}</span>
                      </div>
                      {(added.length > 0 || removed.length > 0) && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {added.map((m) => (
                            <Badge key={`a-${m}`} className="bg-green-100 text-green-800 text-xs">+ {moduleName(m)}</Badge>
                          ))}
                          {removed.map((m) => (
                            <Badge key={`r-${m}`} className="bg-red-100 text-red-800 text-xs">− {moduleName(m)}</Badge>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        by {log.actor_name || 'System'}{log.actor_email ? ` (${log.actor_email})` : ''}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccessLog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
